import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { TRAINING_DOMAINS } from "../data/constants";
import { C } from "../theme/colors";
import Divider from "../components/Divider";
import Section from "../components/Section";
import PageHero from "../components/PageHero";
import CyberCourseDetailPage from "./CyberCourseDetailPage";

const TRAINING_API_URL = "https://ccapi.rapifuzz.in:8443/api/training/";
const API_BASE = "https://ccapi.rapifuzz.in:8443";

export default function TrainingPage() {
  const { subpage: urlSubpage } = useParams();
  const navigate = useNavigate();
  const subpage = urlSubpage || "overview";
  const setSubpage = (id) => navigate(id === "overview" ? "/training" : `/training/${id}`);
  const [apiData, setApiData] = useState(null);

  useEffect(() => {
    let cancelled = false;

    fetch(TRAINING_API_URL)
      .then(res => {
        if (!res.ok) throw new Error(`Request failed (${res.status})`);
        return res.json();
      })
      .then(json => {
        if (cancelled) return;
        const record = Array.isArray(json?.data) ? json.data[0] : null;
        if (record) setApiData(record);
      })
      .catch(() => {
        // Silently keep static fallback data on any failure
      });

    return () => { cancelled = true; };
  }, []);

  if (subpage === "cybersecurity") {
    return <CyberCourseDetailPage onBack={() => setSubpage("overview")}/>;
  }

  // ── Derived data: prefer live API data, fall back to static content ──
  const heroSubtitle = apiData?.sub_title
    || "Skill development programs for national security professionals — custom-designed certification courses for working professionals across India's defence, government, and security sectors.";

  const overviewHeading = apiData?.heading
    || "Training of Working Professionals with Custom-Designed Certification Courses";

  const overviewDescription = apiData?.sub_description || null;

  const staticCertifications = [
    {color:"#1A3A8F",bg:"#E8F0FE",label:"Technology",desc:"Hands-on technical programs in cybersecurity, AI, quantum computing, and emerging defence technologies"},
    {color:"#0E7490",bg:"#E0F7F8",label:"Management",desc:"Strategic courses on national security policy, risk management, and defence procurement"},
    {color:"#5B21B6",bg:"#F3E8FF",label:"Leadership",desc:"Executive programs for senior officials on national security decision making and inter-agency coordination"},
  ];
  const certColors = [{color:"#1A3A8F",bg:"#E8F0FE"},{color:"#0E7490",bg:"#E0F7F8"},{color:"#5B21B6",bg:"#F3E8FF"}];
  const certifications = apiData?.certifications?.length
    ? apiData.certifications.map((c,i) => ({
        label: c.course_name,
        desc: c.course_desc,
        ...certColors[i % certColors.length],
      }))
    : staticCertifications;

  const domains = apiData?.domains?.length
    ? apiData.domains.map(d => ({ label: d.domain_name, icon: d.image ? `${API_BASE}${d.image}` : null }))
    : TRAINING_DOMAINS;

  const staticCourses = [
    {
      isOpen: true,
      slug: "professional-development-course-in-cybersecurity",
      offer_heading: "Enrolment Open · Cohort 2",
      offer_title: "Professional Development Course in Cybersecurity",
      tags: ["8 Weeks","Online","CRENS × CERT-In"],
      course_desc: "8-week online certification covering cybersecurity fundamentals, threat intelligence, ethical hacking, and national security applications. Jointly certified by CRENS and CERT-In.",
      course_btn: "View Course Details",
    },
    {
      isOpen: false,
      slug: "course-on-quantum-communications",
      offer_heading: "Coming Soon",
      offer_title: "Course on Quantum Communications",
      tags: ["Duration TBA","Online","CRENS"],
      course_desc: "Quantum communication protocols, QKD, and post-quantum cryptography for secure defence communications.",
      course_btn: null,
    },
  ];
  const courses = apiData?.courses?.length
    ? apiData.courses.map(c => ({
        isOpen: !/coming soon/i.test(c.offer_heading || "") && !/coming soon/i.test(c.course_btn || ""),
        slug: c.slug,
        offer_heading: c.offer_heading,
        offer_title: c.offer_title,
        tags: [c.duration, c.mode, c.course_provided].filter(Boolean),
        course_desc: c.course_desc,
        course_btn: /coming soon/i.test(c.course_btn || "") ? null : c.course_btn,
      }))
    : staticCourses;

  return (
    <div className="page-fade">
      <PageHero title="Training" tag="Skill Development" subtitle={heroSubtitle}/>
      <div style={{background:C.white,borderBottom:`1px solid ${C.border}`,padding:"0 40px"}}>
        <div style={{maxWidth:1260,margin:"0 auto",display:"flex",gap:0}}>
          {[
            {id:"overview",label:"Training Overview"},
            {id:"cybersecurity",label:"Professional Development Course in Cybersecurity"},
            {id:"quantum",label:"Quantum Communications Course",soon:true},
          ].map(item => (
            <button key={item.id} onClick={() => !item.soon && setSubpage(item.id)}
              style={{background:"none",border:"none",cursor:item.soon?"default":"pointer",padding:"16px 22px",fontFamily:"Arial,Helvetica,sans-serif",fontSize:13,fontWeight:subpage===item.id?700:500,color:item.soon?C.textMuted:subpage===item.id?C.navy:C.textMid,borderBottom:subpage===item.id?`2.5px solid ${C.navy}`:"2.5px solid transparent",transition:"all .2s",whiteSpace:"nowrap",display:"flex",alignItems:"center",gap:6}}>
              {item.label}
              {item.soon && <span style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:10,color:C.textMuted,fontStyle:"italic"}}>(Coming Soon)</span>}
            </button>
          ))}
        </div>
      </div>

      <div className="page-fade">
        <Section>
          <div style={{marginBottom:48}}>
            <span className="section-tag">Program Design</span>
            <h2 style={{fontFamily:"var(--font-heading)",fontWeight:700,fontSize:"clamp(1.5rem,2.5vw,2.2rem)",color:C.navyDark,marginTop:10}}>{overviewHeading}</h2>
            <Divider/>
            {overviewDescription ? (
              <div className="body" style={{maxWidth:720,marginBottom:28}} dangerouslySetInnerHTML={{__html: overviewDescription}} />
            ) : (
              <p className="body" style={{maxWidth:720,marginBottom:28}}>CRENS designs and delivers specialised certification programs for working professionals across India's defence, government, and security sectors. Programs are built in direct consultation with stakeholder agencies to address real operational capability gaps.</p>
            )}
            <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
              {certifications.map((t,i) => (
                <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 16px",borderRadius:8,background:t.bg,border:`1.5px solid ${t.color}22`}}>
                  <span style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:12,fontWeight:700,padding:"3px 10px",borderRadius:4,background:t.color,color:"#fff"}}>{t.label}</span>
                  <span style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:13,color:C.textMid}}>{t.desc}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:24,marginBottom:16}} className="grid-2">
            {courses.map((c,i) => (
              c.isOpen ? (
                <div key={i} style={{background:C.white,border:`1.5px solid #D97706`,borderRadius:14,overflow:"hidden",cursor:"pointer",transition:"box-shadow .2s"}}
                  onClick={() => setSubpage("cybersecurity")}
                  onMouseEnter={e=>e.currentTarget.style.boxShadow="0 8px 28px rgba(15,37,87,0.14)"}
                  onMouseLeave={e=>e.currentTarget.style.boxShadow="none"}>
                  <div style={{background:"#FFFBEB",padding:"12px 20px",borderBottom:`1px solid #FDE68A`,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                    <span style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:11,fontWeight:700,background:"#D97706",color:"#fff",padding:"3px 10px",borderRadius:4,letterSpacing:0.5}}>{c.offer_heading}</span>
                  </div>
                  <div style={{padding:"24px"}}>
                    <h3 style={{fontFamily:"var(--font-heading)",fontSize:17,fontWeight:700,color:C.navyDark,marginBottom:10,lineHeight:1.3}}>{c.offer_title}</h3>
                    <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:14}}>
                      {c.tags.map((t,ti) => <span key={ti} style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:11,color:C.textMuted,background:C.bgAlt,padding:"4px 10px",borderRadius:20,border:`1px solid ${C.border}`}}>{t}</span>)}
                    </div>
                    <p style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:13.5,color:C.textMid,lineHeight:1.75,marginBottom:18}}>{c.course_desc}</p>
                    <span style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:13.5,fontWeight:600,color:C.navy}}>{c.course_btn || "View Course Details"} →</span>
                  </div>
                </div>
              ) : (
                <div key={i} style={{background:C.white,border:`1px solid ${C.border}`,borderRadius:14,overflow:"hidden",opacity:0.8}}>
                  <div style={{background:C.bgAlt,padding:"12px 20px",borderBottom:`1px solid ${C.borderLight}`,display:"flex",alignItems:"center"}}>
                    <span style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:11,fontWeight:700,background:C.textMuted,color:"#fff",padding:"3px 10px",borderRadius:4}}>{c.offer_heading}</span>
                  </div>
                  <div style={{padding:"24px"}}>
                    <h3 style={{fontFamily:"var(--font-heading)",fontSize:17,fontWeight:700,color:C.navyDark,marginBottom:10,lineHeight:1.3}}>{c.offer_title}</h3>
                    <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:14}}>
                      {c.tags.map((t,ti) => <span key={ti} style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:11,color:C.textMuted,background:C.bgAlt,padding:"4px 10px",borderRadius:20,border:`1px solid ${C.border}`}}>{t}</span>)}
                    </div>
                    <p style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:13.5,color:C.textMid,lineHeight:1.75}}>{c.course_desc}</p>
                  </div>
                </div>
              )
            ))}
          </div>
        </Section>
        <div style={{background:C.bgAlt,padding:"64px 40px"}}>
          <div style={{maxWidth:1260,margin:"0 auto"}}>
            <div style={{textAlign:"center",marginBottom:40}}>
              <span className="section-tag">Domains</span>
              <h2 style={{fontFamily:"var(--font-heading)",fontWeight:700,fontSize:"clamp(1.4rem,2.5vw,2rem)",color:C.navyDark,marginTop:10}}>Training Domains</h2>
              <Divider/>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(130px,1fr))",gap:14}}>
              {domains.map((d,i) => (
                <div key={i} className="domain-chip">
                  {d.icon && (
                    <img
                      src={d.icon}
                      alt={d.label}
                      style={{
                        width: 30,
                        height: 30,
                        objectFit: "contain",
                      }}
                    />
                  )}
                  <span style={{fontFamily:"Arial,Helvetica,sans-serif",fontSize:12,fontWeight:600,color:C.navyDark,lineHeight:1.3,textAlign:"center"}}>{d.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}